import type { Theme } from '@emotion/react';
import type { IChangeEvent } from '@rjsf/core';
import type { RJSFSchema, RegistryWidgetsType, WidgetProps } from '@rjsf/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLocale, resolveConnectionInfoSchemaI18n } from '@utils/JsonSchemaUtil';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { MenuItem, Select, TextField, css } from '@mui/material';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { Dialog } from '@components/Dialog';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { FormItemContainer } from '@components/form/FormItemContainer';
import { FormLabel } from '@components/form/FormLabel';
import { Button, Dropdown } from '@components/index';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { DottedLine } from '@icons/DottedLine';
import {
  useGetProductConnector,
  useGetProductConnectorList,
  useGetProductConnectorsBySubscription,
} from '@queryHooks/useProductConnector';
import { useAddSubscriptionConnection, useUpdateSubscriptionConnection } from '@queryHooks/useSubscriptionConnection';
import type { SubscriptionModel } from '@models/SubscriptionModel';
import type { SubscriptionConnectionModel } from '@repositories/subscriptionConnectionRepository/models/SubscriptionConnectionModel';

interface Props {
  subscription: SubscriptionModel;
  connection?: SubscriptionConnectionModel;
  type: 'new' | 'modify';
  open: boolean;
  onClose: (oauth?: boolean) => void;
  onSubmit?: (data: SubscriptionConnectionModel) => void;
}

export const AppLogin = ({ subscription, connection, open, onClose, type, onSubmit }: Props) => {
  const { t } = useTranslation();
  const params = useParams();
  const { subscriptionId } = params;

  const {
    uiStore: { alertModalStore, openGlobalLoader, closeGlobalLoader },
  } = useStore();

  const { data, isLoading, isError } = useGetProductConnectorsBySubscription(subscriptionId as string, {
    cacheTime: 0,
    enabled: open,
  });

  if (!open) {
    return null;
  }

  if (isLoading) {
    openGlobalLoader();
    return null;
  }
  closeGlobalLoader();

  if (isError) {
    alertModalStore.open('Error');
    onClose();
    return null;
  }

  if (data.content.length === 0) {
    alertModalStore.open(t('Common.Connector_Not_Exist'));
    onClose();
    return null;
  }

  return (
    <AppLoginInner
      subscription={subscription}
      connection={connection}
      open={open}
      onClose={onClose}
      type={type}
      onSubmit={onSubmit}
    />
  );
};

const AppLoginInner = ({ subscription, connection, open, onClose, type, onSubmit }: Props) => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const {
    uiStore: { toastStore },
  } = useStore();
  const { subscriptionId } = params;

  const [jsonForm, setJsonForm] = useState({});
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | undefined>(
    connection?.connector?.connectorId,
  );

  const { data: selectedConnector } = useGetProductConnector(selectedConnectorId ?? '', {
    enabled: !!selectedConnectorId,
  });
  const { data: connectorPageData, isLoading: isConnectorListLoading } = useGetProductConnectorList({
    productId: subscription.software.id,
  });
  const connectors = connectorPageData?.content ?? [];

  const { mutate: addConnection, isPending: isAddConnectionPending } = useAddSubscriptionConnection();
  const { mutate: updateConnector, isPending: isUpdateConnectorPending } = useUpdateSubscriptionConnection(
    subscriptionId as string,
  );

  useEffect(() => {
    if (!isConnectorListLoading && (connectorPageData?.content?.length ?? 0) > 0 && !selectedConnectorId) {
      setSelectedConnectorId(connectorPageData?.content[0].connectorId);
    }
  }, [connectorPageData?.content, isConnectorListLoading, selectedConnectorId]);

  useEffect(() => {
    if (selectedConnector) {
      try {
        setJsonForm(JSON.parse(selectedConnector.connectionInfoSchema));
      } catch {
        if (import.meta.env.DEV) {
          console.error('Failed to parse connection info schema');
        }
        toastStore.open(t('Subscrib_Connect_Create_27'));
        setJsonForm({});
      }
    } else {
      setJsonForm({});
    }
  }, [selectedConnector, selectedConnectorId, toastStore, t]);

  const handleSubmit = (data: IChangeEvent<any, RJSFSchema, any>) => {
    // 제출 시점의 해석된 스키마(디펜던시 반영) 기준으로 보이는 필드만 포함, 빈 값 제거
    const resolvedSchema = data.retrievedSchema as RJSFSchema | undefined;
    const visibleKeys = new Set(Object.keys(resolvedSchema?.properties ?? {}));

    const cleanedFormData = Object.fromEntries(
      Object.entries(data.formData ?? {}).filter(
        ([key, value]) =>
          visibleKeys.has(key) && value !== '' && value !== null && value !== undefined,
      ),
    );

    if (type === 'new') {
      addConnection(
        {
          subscriptionId: subscriptionId as string,
          connectorId: selectedConnector?.connectorId,
          connectionInfo: cleanedFormData,
        },
        {
          onSuccess: conn => {
            onClose();
            if (onSubmit) onSubmit(conn);
          },
        },
      );
    } else {
      if (!connection) {
        toastStore.open({ message: t('Subscrib_Connect_Create_26') });
        return;
      }

      updateConnector(
        { subscriptionId: subscriptionId as string, connectionInfo: cleanedFormData },
        {
          onSuccess: conn => {
            onClose();
            if (onSubmit) onSubmit(conn);
          },
        },
      );
    }
  };

  const handleChangeConnector = (value: string | number) => {
    setSelectedConnectorId(value as string);
  };

  /**
   * Widget 종류들 - https://rjsf-team.github.io/react-jsonschema-form/docs/advanced-customization/custom-widgets-fields/#customizing-the-default-fields-and-widgets
   */
  const widgets: RegistryWidgetsType = {
    TextWidget: CustomTextWidget,
    SelectWidget: CustomSelectWidget,
  };

  const formData = connection?.connection.connectionInfo ?? {};

  const formSchema = useMemo(
    () => resolveConnectionInfoSchemaI18n(jsonForm, normalizeLocale(i18n.language)),
    [jsonForm, i18n.language],
  );

  return (
    <StyledDialog title={t('Subscrib_Connect_Create_03')} size="small" open={open} onClose={onClose} useCustomContent>
      <StyledDialogBody $hasFooter>
        <FormItemContainer>
          <FormLabel>{t('Subscrib_Connect_Select_Connector')}</FormLabel>
          <Dropdown value={selectedConnectorId} onChange={handleChangeConnector} disabled={type !== 'new'}>
            {connectors.map(connector => (
              <Dropdown.Menu key={connector.connectorId} value={connector.connectorId}>
                {connector.name}
              </Dropdown.Menu>
            ))}
          </Dropdown>
        </FormItemContainer>
        <DottedLine />
        <StyledForm
          schema={formSchema}
          validator={validator}
          formData={formData}
          onSubmit={handleSubmit}
          uiSchema={{
            'ui:globalOptions': {
              label: true,
            },
            'ui:submitButtonOptions': {
              norender: true,
            },
          }}
          widgets={widgets}
        >
          <Dialog.Footer>
            <SecureSubmitButton
              variant="contain"
              type="submit"
              size="extraLarge"
              isPending={isAddConnectionPending || isUpdateConnectorPending}
            >
              {t('Member_User_24')}
            </SecureSubmitButton>
            <Button variant="outline" size="extraLarge" onClick={() => onClose()}>
              {t('Subscrib_Detail_BuyRequest_13')}
            </Button>
          </Dialog.Footer>
        </StyledForm>
      </StyledDialogBody>
    </StyledDialog>
  );
};

export const CustomTextWidget = ({ schema, required, value, onChange, onFocus, label }: WidgetProps) => {
  return (
    <TextWidgetContainer>
      <span className="title">
        {schema.title || label} {required ? <strong>*</strong> : ''}
      </span>
      <TextField
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={e => onFocus(label, e.target.value)}
        placeholder={schema.title}
        variant="outlined"
        size="medium"
      />
    </TextWidgetContainer>
  );
};

export const CustomSelectWidget = ({ schema, required, value, onChange, label }: WidgetProps) => {
  const enumValues = schema?.enum ?? [];
  const enumNames = (schema as RJSFSchema & { enumNames?: string[] })?.enumNames;
  const options = enumValues.map((enumVal, idx) => ({
    value: (enumVal ?? idx).toString(),
    label: enumNames?.[idx] ?? (enumVal ?? idx).toString(),
  }));

  return (
    <SelectWidgetContainer>
      <span className="title">
        {schema.title || label} {required ? <strong>*</strong> : ''}
      </span>
      <Select
        value={value ?? ''}
        onChange={e => {
          const { value: nextValue } = e.target;
          onChange(nextValue);
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxWidth: '416px',
              width: '100%',
              marginTop: '6px',
            },
          },
        }}
      >
        {options.map(({ value: optValue, label: optLabel }) => (
          <MenuItem key={optValue} value={optValue}>
            {optLabel}
          </MenuItem>
        ))}
      </Select>
    </SelectWidgetContainer>
  );
};

const StyledForm = styled(Form)`
  max-height: 416px;

  & .MuiGrid-container {
    margin-top: 0px !important;

    & .MuiGrid-item {
      padding: 0px;
    }
  }
  & .MuiTypography-root {
    ${fonts.Body4}
    color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  }
`;

const WidgetCommonStyles = ({ colors }: Theme) => css`
  display: flex;
  flex-direction: column;

  & .title {
    margin-bottom: 8px;
    ${fonts.Headline8}
    color: ${colors['text-gray-main']};

    & > strong {
      color: ${colors['text-red-light']};
    }
  }
`;

const TextWidgetContainer = styled.div`
  ${({ theme }) =>
    css`
      ${WidgetCommonStyles(theme)}
    `}

  & .MuiInputLabel-root {
    ${fonts.Body2}
    top: -6px;
  }

  & .MuiOutlinedInput-notchedOutline {
    margin-top: 4px;
    height: 100%;

    & > legend {
      display: none;
    }
  }

  & .MuiFormLabel-root {
    display: none;
  }
`;

const SelectWidgetContainer = styled.div`
  ${({ theme }) =>
    css`
      ${WidgetCommonStyles(theme)}
    `};
`;

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paperWidthSm {
    width: 416px;
    height: 576px;
    margin: 0px;
  }
  & .MuiGrid-root {
    width: 100%;
  }
  & .MuiGrid-container {
    margin-left: 0px;
    max-width: 416px;
  }
`;

const StyledDialogBody = styled(Dialog.Body)`
  padding: 12px 28px 0px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
